import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { createSupabaseClientWithStorage, supabase } from "@/lib/supabase";
import {
  createSessionBridgeStatusMessage,
  isSessionBridgePingMessage,
  resolveSessionBridgeParentOrigin,
} from "@/lib/session-bridge";

/** Request unpartitioned localStorage via Storage Access API; returns it if granted. */
async function requestUnpartitionedLocalStorage(): Promise<Storage | null> {
  const doc = document as Document & {
    requestStorageAccess?: (options?: { localStorage?: boolean }) => Promise<{ localStorage: Storage } | undefined>;
  };
  if (typeof doc.requestStorageAccess !== "function") {
    return null;
  }
  try {
    const result = await doc.requestStorageAccess({ localStorage: true });
    if (result?.localStorage) {
      return result.localStorage;
    }
    return null;
  } catch {
    return null;
  }
}

const sessionBridgeSearchSchema = z.object({
  parentOrigin: z.string().optional(),
});

export const Route = createFileRoute("/session-bridge")({
  component: SessionBridgeRoute,
  validateSearch: sessionBridgeSearchSchema,
});

type BridgeState = "ready" | "missing-parent" | "invalid-origin";

function SessionBridgeRoute() {
  const search = useSearch({ from: "/session-bridge" });
  const [bridgeState, setBridgeState] = useState<BridgeState>("ready");

  const parentOrigin = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return resolveSessionBridgeParentOrigin(
      search.parentOrigin,
      document.referrer,
    );
  }, [search.parentOrigin]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (window.parent === window) {
      setBridgeState("missing-parent");
      return undefined;
    }

    if (!parentOrigin) {
      setBridgeState("invalid-origin");
      return undefined;
    }

    setBridgeState("ready");

    let isDisposed = false;

    const SESSION_CHECK_TIMEOUT_MS = 3000;

    // Send an immediate status so the parent knows the bridge is alive (avoids appearing unresponsive in iframes where getSession may hang or throw).
    window.parent.postMessage(
      createSessionBridgeStatusMessage(false),
      parentOrigin,
    );

    const postSessionStatus = async () => {
      const sendStatus = (hasSession: boolean) => {
        if (!isDisposed) {
          window.parent.postMessage(
            createSessionBridgeStatusMessage(hasSession),
            parentOrigin,
          );
        }
      };

      try {
        const storage = await requestUnpartitionedLocalStorage();
        const client = storage ? createSupabaseClientWithStorage(storage) : supabase;
        const sessionPromise = client.auth.getSession();
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Session check timeout")), SESSION_CHECK_TIMEOUT_MS),
        );
        const {
          data: { session },
        } = await Promise.race([sessionPromise, timeoutPromise]);

        sendStatus(Boolean(session?.user));
      } catch (error) {
        console.error("Session bridge failed to inspect the current session", error);
        sendStatus(false);
      }
    };

    void postSessionStatus();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      window.parent.postMessage(
        createSessionBridgeStatusMessage(Boolean(session?.user)),
        parentOrigin,
      );
    });

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== parentOrigin) {
        return;
      }

      if (!isSessionBridgePingMessage(event.data)) {
        return;
      }

      void postSessionStatus();
    };

    window.addEventListener("message", handleMessage);

    return () => {
      isDisposed = true;
      subscription.unsubscribe();
      window.removeEventListener("message", handleMessage);
    };
  }, [parentOrigin]);

  const message =
    bridgeState === "missing-parent"
      ? "Session bridge is waiting to be embedded in a parent page."
      : bridgeState === "invalid-origin"
        ? "Session bridge rejected this parent origin."
        : "Session bridge is ready.";

  return (
    <main className="bg-background text-muted-foreground flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-sm">
      <p>{message}</p>
      {bridgeState === "ready" && (
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={() => {
            void (async () => {
              const storage = await requestUnpartitionedLocalStorage();
              const client = storage ? createSupabaseClientWithStorage(storage) : supabase;
              const { data } = await client.auth.getSession();
              if (parentOrigin) {
                window.parent.postMessage(
                  createSessionBridgeStatusMessage(Boolean(data.session?.user)),
                  parentOrigin,
                );
              }
            })();
          }}
        >
          Refresh session status
        </button>
      )}
    </main>
  );
}
